import { Alert, Button, Modal, Stack, Text, Textarea } from "@mantine/core";
import { useForm } from "@mantine/form";
import { WarningIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ReturnsApi } from "../../apis/returns";
import { message } from "../../utils/Message";
import { handleError } from "../../utils/error-handler";
import { ModalFooter } from "../ui/app-modal";
import modalChrome from "../ui/app-modal.module.css";

interface DisputeModalProps {
  opened: boolean;
  onClose: () => void;
  returnRequest: { id: string; disputeCount: number; returnNumber: string; version?: number };
  onSuccess: () => void;
}

type FormValues = {
  disputeNotes: string;
};

const MAX_DISPUTES = 3;

export function DisputeModal({ opened, onClose, returnRequest, onSuccess }: DisputeModalProps) {
  const { t } = useTranslation("returns");
  const [loading, setLoading] = useState<boolean>(false);

  const form = useForm<FormValues>({
    mode: "controlled",
    initialValues: {
      disputeNotes: "",
    },
  });

  const isMaxReached = returnRequest.disputeCount >= MAX_DISPUTES;

  const handleSubmit = async () => {
    if (!form.values.disputeNotes.trim()) {
      message.error(t("dispute.errNotes"));
      return;
    }

    try {
      setLoading(true);
      await ReturnsApi.dispute(returnRequest.id, {
        disputeNotes: form.values.disputeNotes.trim(),
        expectedVersion: returnRequest.version,
      });
      message.success(t("dispute.success"));
      onSuccess();
      handleClose();
    } catch (error) {
      handleError(error, t("dispute.error"));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={t("dispute.modalTitle")}
      size="md"
      classNames={{ body: modalChrome.modalBodyFlush }}
    >
      <Stack gap="md" px={24} pb={20}>
        <Text size="sm" c="dimmed">
          {t("dispute.requestLabel")} <Text span fw={600} c="gray.9">{returnRequest.returnNumber}</Text>
        </Text>

        <Text size="sm">
          {t("dispute.disputeCount")} <Text span fw={600}>{returnRequest.disputeCount}</Text> / {MAX_DISPUTES}
        </Text>

        {isMaxReached && (
          <Alert icon={<WarningIcon size={16} />} color="red" title={t("dispute.limitTitle")}>
            {t("dispute.limitDesc", { max: MAX_DISPUTES })}
          </Alert>
        )}

        <Textarea
          label={t("dispute.notes")}
          placeholder={t("dispute.notesPlaceholder")}
          {...form.getInputProps("disputeNotes")}
          minRows={3}
          required
          disabled={isMaxReached}
        />
      </Stack>
      <ModalFooter>
        <Button variant="default" onClick={handleClose}>
          {t("action.cancel")}
        </Button>
        <Button color="brand" loading={loading} onClick={handleSubmit} disabled={isMaxReached}>
          {t("dispute.submit")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
